import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { FilterExpression, PerspectiveFilter } from 'types/filters';

export interface SphereSearchResult {
  id: string;
  label: string;
  glyph?: string | null;
  glyphColor?: Record<string, unknown> | null;
  isPrivate?: boolean;
  parentPath?: string[];
  status?: string | null;
  description?: string | null;
  center?: {
    id: string;
    name?: string;
    glyph?: string | null;
    glyphColor?: Record<string, unknown> | null;
  } | null;
  reason?: 'recent' | 'mine' | 'nearby' | 'match' | null;
  depth?: number;
  properties?: {
    type?: string;
    name?: string;
    description?: string;
    imageId?: string;
  };
}

export type SphereSearchSource = 'all' | 'recent' | 'mine' | 'nearby';

export interface SphereSearchOptions {
  source?: SphereSearchSource;
  /** Passed verbatim to the /search request body. Must be a stable reference (memoize in callers). */
  contextFilters?: FilterExpression[];
  /**
   * Per-level filter arrays; maps to the search service `contextLevelFilters` field.
   * Must be a stable reference (memoize in callers).
   */
  contextLevelFilters?: FilterExpression[][];
  /** Passed verbatim to the /search request body. Must be a stable reference (memoize in callers). */
  filterExpressions?: FilterExpression[];
  /**
   * Relationship (perspective-assignment) filters — e.g. `center_parent`,
   * `center_responsible`. Passed verbatim to the /search request body. Must be
   * a stable reference (memoize in callers).
   */
  perspectiveFilters?: PerspectiveFilter[];
  permission?: 'view' | 'edit';
  pageSize?: number;
  nearbyAnchorSphereId?: string;
  /** Increment this value to force a fresh search from offset 0 even when other options are unchanged. */
  resetKey?: number;
  /** When false, the hook skips the request and returns an empty result set. Defaults to true. */
  enabled?: boolean;
  /**
   * Optional other org to search instead of the active one. Used for cross-org
   * reference resolution (e.g. a reference dropdown on a sphere being created in
   * an org the caller administers but doesn't belong to). The caller must have
   * view access to this org or the search service returns 403. Omit to search
   * the active org.
   */
  targetOrganizationId?: string;
}

export interface UseSphereSearchReturn {
  results: SphereSearchResult[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasNextPage: boolean;
  setQuery: (query: string) => void;
  loadMore: () => void;
}

interface SearchApiResponse {
  results: SphereSearchResult[];
  total: number;
  returned: number;
}

const DEBOUNCE_MS = 300;

export function useSphereSearch(options: SphereSearchOptions = {}): UseSphereSearchReturn {
  const {
    source = 'all',
    contextFilters,
    contextLevelFilters,
    filterExpressions,
    perspectiveFilters,
    permission = 'view',
    pageSize = 20,
    nearbyAnchorSphereId,
    resetKey = 0,
    enabled = true,
    targetOrganizationId,
  } = options;

  const apiClients = useApiClientsOrNull();

  const [query, setQueryInternal] = useState('');
  const [results, setResults] = useState<SphereSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [returnedSoFar, setReturnedSoFar] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounce query changes — callers use this instead of setting state directly.
  const setQuery = useCallback((term: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setQueryInternal(term);
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Fresh search whenever query or options change.
  useEffect(() => {
    if (!apiClients?.searchClient) return;

    // Caller opted out (e.g. a dependent field whose sibling is still unset) —
    // skip the request and surface an empty, non-loading result set.
    if (!enabled) {
      setResults([]);
      setTotal(0);
      setReturnedSoFar(0);
      setLoading(false);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);
    setResults([]);
    setTotal(0);
    setReturnedSoFar(0);

    const doSearch = async () => {
      try {
        const data = await apiClients.searchClient.post<SearchApiResponse>('/search', {
          query: query || undefined,
          source,
          nearbyAnchorSphereId: source === 'nearby' ? nearbyAnchorSphereId : undefined,
          contextFilters: contextFilters ?? undefined,
          contextLevelFilters: contextLevelFilters ?? undefined,
          filterExpressions: filterExpressions ?? undefined,
          perspectiveFilters: perspectiveFilters ?? undefined,
          permission,
          limit: pageSize,
          offset: 0,
          targetOrganizationId: targetOrganizationId ?? undefined,
        });
        if (!cancelled) {
          setResults(data.results);
          setTotal(data.total);
          setReturnedSoFar(data.returned);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Search failed');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    doSearch();

    return () => { cancelled = true; };
  }, [query, apiClients, source, contextFilters, contextLevelFilters, filterExpressions, perspectiveFilters, permission, pageSize, nearbyAnchorSphereId, resetKey, enabled, targetOrganizationId]);

  // Append the next page using the accumulated offset.
  const loadMore = useCallback(async () => {
    if (!apiClients?.searchClient || loadingMore) return;

    setLoadingMore(true);
    setError(null);

    try {
      const data = await apiClients.searchClient.post<SearchApiResponse>('/search', {
        query: query || undefined,
        source,
        nearbyAnchorSphereId: source === 'nearby' ? nearbyAnchorSphereId : undefined,
        contextFilters: contextFilters ?? undefined,
        contextLevelFilters: contextLevelFilters ?? undefined,
        filterExpressions: filterExpressions ?? undefined,
        perspectiveFilters: perspectiveFilters ?? undefined,
        permission,
        limit: pageSize,
        offset: returnedSoFar,
        targetOrganizationId: targetOrganizationId ?? undefined,
      });
      setResults(prev => [...prev, ...data.results]);
      setTotal(data.total);
      setReturnedSoFar(prev => prev + data.returned);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load more failed');
    } finally {
      setLoadingMore(false);
    }
  }, [
    apiClients,
    query,
    source,
    nearbyAnchorSphereId,
    contextFilters,
    contextLevelFilters,
    filterExpressions,
    perspectiveFilters,
    permission,
    pageSize,
    returnedSoFar,
    loadingMore,
    targetOrganizationId,
  ]);

  const hasNextPage = total > 0 && returnedSoFar < total;

  return { results, loading, loadingMore, error, hasNextPage, setQuery, loadMore };
}
